/**
 * SearchResults - the result list shared by every search surface.
 *
 * The shells (SearchModal on desktop, MobileSearchPage on the phone) own the
 * input, the debounce and how they open and dismiss; this component owns what
 * a query returns and what selecting it does — grouping, icons, analytics and
 * navigation stay in one place so the two surfaces cannot drift.
 *
 * Must render inside a cmdk <Command> root: Group/Item read the root's
 * context for keyboard selection.
 */

import { useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Command } from 'cmdk';
import { useSelector } from '@legendapp/state/react';
import { ACCOUNT_COMMANDS } from './accountCommands';
import { toast } from 'sonner';
import {
  FileText,
  Highlighter,
  CreditCard,
  Sparkles,
  Clock,
} from 'lucide-react';
import { ui$, getArticleById } from '../../store';
import { ICON_SIZE } from '../../design-system';
import useSearchIndex from '../../hooks/useSearchIndex';
import { trackEvent, setOnceUserProperties } from '../../lib/analytics';

const SearchResults = ({ debouncedQuery, onDismiss, showActions = true, navigateState }) => {
  const { t } = useTranslation(['search', 'common', 'sidebar']);
  const navigate = useNavigate();
  const colorTheme = useSelector(() => ui$.colorTheme.get());
  const { search, getRecentArticles } = useSearchIndex();

  // Search results from MiniSearch + pinyin
  const searchResults = useMemo(() => {
    if (!debouncedQuery.trim()) return [];
    return search(debouncedQuery);
  }, [debouncedQuery, search]);

  // Track debounced queries ≥2 chars
  useEffect(() => {
    if (debouncedQuery.trim().length < 2) return;
    const articleCount = searchResults.filter(r => r._type === 'article').length;
    const highlightCount = searchResults.filter(r => r._type === 'highlight').length;
    const cardCount = searchResults.filter(r => r._type === 'card').length;
    trackEvent('search_query_entered', {
      query_length: debouncedQuery.length,
      result_count: searchResults.length,
      result_types: [
        articleCount > 0 && 'article',
        highlightCount > 0 && 'highlight',
        cardCount > 0 && 'card',
      ].filter(Boolean),
    });
  }, [debouncedQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group results by type
  const articleResults = useMemo(
    () => searchResults.filter(r => r._type === 'article').slice(0, 8),
    [searchResults]
  );
  const highlightResults = useMemo(
    () => searchResults.filter(r => r._type === 'highlight').slice(0, 5),
    [searchResults]
  );
  const cardResults = useMemo(
    () => searchResults.filter(r => r._type === 'card').slice(0, 5),
    [searchResults]
  );

  // Recent articles (empty query state)
  const recentArticles = useMemo(() => {
    if (debouncedQuery.trim()) return [];
    return getRecentArticles(5);
  }, [debouncedQuery, getRecentArticles]);

  // Search the same localized labels that the command rows display.
  const filteredCommands = useMemo(() => {
    const commands = ACCOUNT_COMMANDS.map(cmd => cmd.id === 'cmd:toggle-theme' ? {
      ...cmd,
      labelKey: colorTheme === 'dark' ? 'sidebar:themes.switchToLight' : 'sidebar:themes.switchToDark',
      keywords: [...cmd.keywords, t(cmd.labelKey), t('sidebar:themes.light'), t('sidebar:themes.dark')],
    } : cmd);
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(cmd =>
      cmd.keywords.some(kw => kw.toLowerCase().includes(q)) ||
      t(cmd.labelKey).toLowerCase().includes(q)
    );
  }, [debouncedQuery, t, colorTheme]);

  // AI suggestion slots (static, shown when query is non-empty)
  // TODO: Dynamic AI suggestions via semantic search (v2)
  const aiSuggestions = useMemo(() => {
    if (!debouncedQuery.trim()) return [];
    return [
      {
        id: 'ai:ask',
        label: t('search:ai.askAbout', { query: debouncedQuery }),
        action: () => toast(t('search:ai.comingSoon')),
      },
      {
        id: 'ai:summarize',
        label: t('search:ai.summarize', { query: debouncedQuery }),
        action: () => toast(t('search:ai.comingSoon')),
      },
      {
        id: 'ai:cards',
        label: t('search:ai.generateCards', { query: debouncedQuery }),
        action: () => toast(t('search:ai.comingSoon')),
      },
    ];
  }, [debouncedQuery, t]);

  // Shared helper to fire selection event + first_search milestone
  const trackSelection = useCallback((resultType) => {
    trackEvent('search_result_selected', { result_type: resultType, query_length: debouncedQuery.length });
    setOnceUserProperties({ first_search_used_at: new Date().toISOString() });
    trackEvent('first_search_used', { result_type: resultType });
  }, [debouncedQuery]);

  // Handle article selection
  const handleArticleSelect = useCallback((articleId, isRecent = false) => {
    trackSelection(isRecent ? 'recent' : 'article');
    onDismiss();
    navigate(`/article/${articleId}`, { state: navigateState });
  }, [navigate, navigateState, onDismiss, trackSelection]);

  // Handle highlight selection — open the source article scrolled to the mark
  const handleHighlightSelect = useCallback((result) => {
    trackSelection('highlight');
    onDismiss();
    if (result.article_id) {
      navigate(result.originalId
        ? `/article/${result.article_id}#highlight-${result.originalId}`
        : `/article/${result.article_id}`, { state: navigateState });
    }
  }, [navigate, navigateState, onDismiss, trackSelection]);

  // Handle card selection — same anchor as highlights when the card was grown
  // from one, then deck / article as fallbacks
  const handleCardSelect = useCallback((card) => {
    trackSelection('card');
    onDismiss();
    if (card.source_article_id && card.source_highlight_id) {
      navigate(`/article/${card.source_article_id}#highlight-${card.source_highlight_id}`, { state: navigateState });
    } else if (card.source_article_id) {
      navigate(`/article/${card.source_article_id}`, { state: navigateState });
    } else if (card.deck_id) {
      navigate(`/deck/${card.deck_id}`, { state: navigateState });
    }
  }, [navigate, navigateState, onDismiss, trackSelection]);

  // Handle AI suggestion selection
  const handleAiSelect = useCallback((suggestion) => {
    trackSelection('ai');
    onDismiss();
    suggestion.action();
  }, [onDismiss, trackSelection]);

  // Handle command selection
  const handleCommandSelect = useCallback((cmd) => {
    trackSelection('command');
    onDismiss();
    cmd.action(navigate, t);
  }, [navigate, onDismiss, t, trackSelection]);

  // Determine if we should show the custom empty state
  const hasQuery = debouncedQuery.trim().length > 0;
  const hasSearchResults = articleResults.length > 0 || highlightResults.length > 0 || cardResults.length > 0;
  const showNoResults = hasQuery && !hasSearchResults && !(showActions && filteredCommands.length > 0);

  return (
    <Command.List className="search-modal-results">
      {/* No results message */}
      {showNoResults && (
        <div className="search-modal-no-results">
          {t('search:noResults')}
        </div>
      )}

      {/* Recent (empty query) */}
      {recentArticles.length > 0 && (
        <Command.Group heading={t('search:groups.recent')} className="search-modal-group">
          {recentArticles.map(article => (
            <Command.Item
              key={`recent:${article.id}`}
              value={`recent:${article.id}`}
              onSelect={() => handleArticleSelect(article.id, true)}
              className="search-modal-item"
            >
              <Clock size={ICON_SIZE.sm} className="search-modal-item-icon" aria-hidden="true" />
              <div className="search-modal-item-content">
                <span className="search-modal-item-title">
                  {article.title || t('common:common.untitled')}
                </span>
                {article.site_name && (
                  <span className="search-modal-item-meta">{article.site_name}</span>
                )}
              </div>
            </Command.Item>
          ))}
        </Command.Group>
      )}

      {/* Articles */}
      {articleResults.length > 0 && (
        <Command.Group heading={t('search:groups.articles')} className="search-modal-group">
          {articleResults.map(result => (
            <Command.Item
              key={result.id}
              value={result.id}
              onSelect={() => handleArticleSelect(result.originalId)}
              className="search-modal-item"
            >
              <FileText size={ICON_SIZE.sm} className="search-modal-item-icon" aria-hidden="true" />
              <div className="search-modal-item-content">
                <span className="search-modal-item-title">{result.title || t('common:common.untitled')}</span>
                {result.site_name && (
                  <span className="search-modal-item-meta">{result.site_name}</span>
                )}
              </div>
              {/* TODO: Article cover thumbnails in results */}
              {/* TODO: Tag badges on article results */}
            </Command.Item>
          ))}
        </Command.Group>
      )}

      {/* Highlights */}
      {highlightResults.length > 0 && (
        <Command.Group heading={t('search:groups.highlights')} className="search-modal-group">
          {highlightResults.map(result => {
            const article = getArticleById(result.article_id);
            return (
                    <Command.Item
                      key={result.id}
                      value={result.id}
                      onSelect={() => handleHighlightSelect(result)}
                      className="search-modal-item"
                    >
                <Highlighter size={ICON_SIZE.sm} className="search-modal-item-icon" aria-hidden="true" />
                <div className="search-modal-item-content">
                  <span className="search-modal-item-title">
                    {result.exact_text?.length > 80
                      ? result.exact_text.slice(0, 80) + '...'
                      : result.exact_text}
                  </span>
                  <span className="search-modal-item-meta">
                    {t('search:resultMeta.highlight')} {article?.title || t('search:resultMeta.unknownArticle')}
                  </span>
                </div>
              </Command.Item>
            );
          })}
        </Command.Group>
      )}

      {/* Cards */}
      {cardResults.length > 0 && (
        <Command.Group heading={t('search:groups.cards')} className="search-modal-group">
          {cardResults.map(result => (
            <Command.Item
              key={result.id}
              value={result.id}
              onSelect={() => handleCardSelect(result)}
              className="search-modal-item"
            >
              <CreditCard size={ICON_SIZE.sm} className="search-modal-item-icon" aria-hidden="true" />
              <div className="search-modal-item-content">
                <span className="search-modal-item-title">{result.front}</span>
                <span className="search-modal-item-meta">{t('search:resultMeta.card')}</span>
              </div>
            </Command.Item>
          ))}
        </Command.Group>
      )}

      {/* AI Suggestions */}
      {aiSuggestions.length > 0 && (
        <Command.Group heading={t('search:groups.aiSuggestions')} className="search-modal-group">
          {aiSuggestions.map(suggestion => (
            <Command.Item
              key={suggestion.id}
              value={suggestion.id}
              onSelect={() => handleAiSelect(suggestion)}
              className="search-modal-item"
            >
              <Sparkles size={ICON_SIZE.sm} className="search-modal-item-icon search-modal-item-icon--ai" aria-hidden="true" />
              <span className="search-modal-item-title">{suggestion.label}</span>
            </Command.Item>
          ))}
        </Command.Group>
      )}

      {/* Actions (Commands) — desktop only: the phone reaches account actions
          through Me, and a logout reachable from a search typo is a hazard */}
      {showActions && filteredCommands.length > 0 && (
        <Command.Group heading={t('search:groups.actions')} className="search-modal-group">
          {filteredCommands.map(cmd => {
            const Icon = cmd.icon;
            return (
              <Command.Item
                key={cmd.id}
                value={cmd.id}
                onSelect={() => handleCommandSelect(cmd)}
                className="search-modal-item"
              >
                <Icon size={ICON_SIZE.sm} className="search-modal-item-icon" aria-hidden="true" />
                <div className="search-modal-item-content">
                  <span className="search-modal-item-title">{t(cmd.labelKey)}</span>
                  {cmd.descriptionKey && (
                    <span className="search-modal-item-meta">{t(cmd.descriptionKey)}</span>
                  )}
                </div>
              </Command.Item>
            );
          })}
        </Command.Group>
      )}

      {/* TODO: Search history / frecency scoring */}
      {/* TODO: Tab to cycle between groups */}
    </Command.List>
  );
};

export default SearchResults;
